import {
  HitTrackerConfigInterface,
  HitTrackerRequestParametersKeysType,
  HitTrackerRequestParametersType,
  TrackerConfigMappingType,
} from './types'
import { TrackerTypesApiPaths } from './types/tracker'

import { constructUrlWithPayload } from './utils/constructUrlWithPayload'
import { Cookies } from './utils/cookies'
import { getConfigValue, getDomainConfigValue } from './utils/getConfigValue'
import { getUrlParameters } from './utils/getUrlParameters'
import { isBrowser } from './utils/isBrowser'
import { removeLastSlash } from './utils/removeLastSlash'
import { COOKIE_EXPIRY, DEFAULT_COOKIE_PATH, DELETE_COOKIE_EXPIRY } from './constants'

const defaultConfig: HitTrackerConfigInterface = {
  URLFieldsToAPIFieldsMap: undefined,
  cookieDomain: isBrowser() ? () => `.${window.location.host}` : '',
  errorCallbackOnHit: console.error,
  host: isBrowser() ? () => `https://api.partner.${window.location.host}` : '',
}
let currentConfig: HitTrackerConfigInterface = defaultConfig

/** Tracked data value */
const defaultData: HitTrackerRequestParametersType = {
  aff_id: -1,
}
let currentData: HitTrackerRequestParametersType = defaultData

/** Default {[URL param]: API param} mapping */
const defaultURLFieldsToAPIFieldsMap: TrackerConfigMappingType = {
  advertiser_id: 'advertiser_id',
  aff: 'aff_id',
  aff_model: 'aff_model',
  afftrack: 'aff_track',
  brand_id: 'brand_id',
}

const getURLFieldsToAPIFieldsMap = (): TrackerConfigMappingType => ({
  ...defaultURLFieldsToAPIFieldsMap,
  ...currentConfig.URLFieldsToAPIFieldsMap,
})

/*** Get tracker data */
const getTrackedDataValue = (
  newData?: HitTrackerRequestParametersType,
  urlParameters?: URLSearchParams,
): HitTrackerRequestParametersType => {
  const newTrackedDataValue: HitTrackerRequestParametersType = defaultData
  const URLFieldsToAPIFieldsMap = getURLFieldsToAPIFieldsMap()

  for (const urlParameter in URLFieldsToAPIFieldsMap) {
    const apiParameter: HitTrackerRequestParametersKeysType = URLFieldsToAPIFieldsMap[urlParameter]

    if (apiParameter) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      newTrackedDataValue[apiParameter] =
        newData?.[apiParameter] || // Try to read from newData
        getConfigValue(currentConfig.paramsReader?.[apiParameter]) || // Fallback to read with paramsReader functions
        urlParameters?.get(urlParameter) || // Fallback to read from URL
        Cookies.getCookie(apiParameter) || // Fallback to read from cookies
        undefined
    }
  }

  return newTrackedDataValue
}

/** Write tracked data values to cookies by `apiParameter` name */
const writeTrackedDataValueToCookies = (data: HitTrackerRequestParametersType) => {
  const domain = getDomainConfigValue(currentConfig.cookieDomain)

  for (const apiParameter in data) {
    const typedApiParameter = apiParameter as HitTrackerRequestParametersKeysType

    Cookies.setCookie(apiParameter, currentData[typedApiParameter] ? String(currentData[typedApiParameter]) : '', {
      DEFAULT_COOKIE_PATH,
      domain,
      expires: currentData[typedApiParameter] || '' ? COOKIE_EXPIRY : DELETE_COOKIE_EXPIRY,
    })
  }
}

/** Init config */
const init = (config: HitTrackerConfigInterface) => {
  currentConfig = {
    ...defaultConfig,
    ...config,
  }

  currentData = getTrackedDataValue(undefined, getUrlParameters())

  // Write data to cookies only if `domain` is set
  writeTrackedDataValueToCookies(currentData)
}

/** Send hit */
const hit = (data?: HitTrackerRequestParametersType, fetchConfig?: RequestInit) => {
  const host = getConfigValue(currentConfig.host)

  if (host === null || host === undefined || typeof host !== 'string') {
    console.error('[AffiliateTracker] Missing host')

    return
  }

  if (!isBrowser()) {
    console.info('[AffiliateTracker] SSR is not supported')

    return
  }

  if (typeof fetch === 'undefined') {
    console.error('[AffiliateTracker] "fetch" is not supported')

    return
  }

  const payload = getTrackedDataValue({ ...currentData, ...data })
  const preparedURL = `${removeLastSlash(host)}${TrackerTypesApiPaths.GetApiTH}`

  fetch(constructUrlWithPayload(preparedURL, payload).toString(), {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...fetchConfig?.headers,
    },
    method: 'GET',
    ...fetchConfig,
  })
    .then((response) => response.json())
    .catch(console.error)
}

/**
 * Tracked data value reader
 */
const trackedDataValueReader = () => {
  const trackedDataValue = getTrackedDataValue({ ...currentData })

  return {
    getAdvertiserId: () => (trackedDataValue.advertiser_id ? String(trackedDataValue.advertiser_id) : undefined),
    getAffId: () => (trackedDataValue.aff_id ? String(trackedDataValue.aff_id) : undefined),
    getAffModel: () => (trackedDataValue.aff_model ? String(trackedDataValue.aff_model) : undefined),
    getAffTrack: () => (trackedDataValue.aff_track ? String(trackedDataValue.aff_track) : undefined),
    getBrandId: () => (trackedDataValue.brand_id ? String(trackedDataValue.brand_id) : undefined),
  }
}

const setupInBrowser = () => {
  if (isBrowser()) {
    window.AffiliateHitTracker = {
      hit,
      init,
      trackedDataValueReader,
      /**
       * Current config
       * @readonly
       */
      _readConfig: () => currentConfig,
      /**
       * Current data
       * @readonly
       */
      _readCurrentData: () => currentData,
      /**
       * Default {[URL param]: API param} mapping
       * @readonly
       */
      _readDefaultURLFieldsToAPIFieldsMap: () => defaultURLFieldsToAPIFieldsMap,
    }
  }
}

setupInBrowser()

export default { hit, init, setupInBrowser, trackedDataValueReader }
export { hit, init, setupInBrowser, trackedDataValueReader }
